import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CircleNotch } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

export default function Register() {
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      login(data.token, data.user);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA]">
      <Seo title="Skapa konto – Senzum AI" description="Skapa din arbetsyta på Senzum AI och lansera din första AI-kundtjänstagent på minuter. Gratis att börja." noindex />
      <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-bg opacity-40" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <img src="/senzum-logo-white.png" alt="Senzum AI" className="h-8 w-auto" />
        </Link>
        <div className="relative">
          <h2 className="font-display font-bold text-4xl leading-tight tracking-tight">
            Börja bygga<br />intelligenta agenter.
          </h2>
          <p className="mt-4 text-zinc-400 text-lg">Skapa din arbetsyta och lansera din första AI-kundtjänstagent på minuter.</p>
        </div>
        <div className="relative text-zinc-500 text-sm">© 2026 Senzum AI</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <h1 className="font-display font-bold text-3xl tracking-tight">Skapa ditt konto</h1>
          <p className="mt-2 text-zinc-500">Gratis att börja. Inget kreditkort krävs.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {error && (
              <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Fullständigt namn</label>
              <input data-testid="register-name-input" required value={form.name} onChange={set("name")}
                className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all"
                placeholder="Anna Andersson" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Företag</label>
              <input data-testid="register-company-input" value={form.company_name} onChange={set("company_name")}
                className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all"
                placeholder="Acme Support AB" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">E-post</label>
              <input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")}
                className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all"
                placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Lösenord</label>
              <input data-testid="register-password-input" type="password" required value={form.password} onChange={set("password")}
                className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all"
                placeholder="Minst 6 tecken" />
            </div>
            <button data-testid="register-submit-button" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-6 py-2.5 font-semibold transition-colors disabled:opacity-60">
              {loading && <CircleNotch size={18} className="animate-spin" />} Skapa konto
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-zinc-500">
            Har du redan ett konto?{" "}
            <Link data-testid="register-to-login" to="/login" className="font-semibold text-[#0033FF] hover:underline">
              Logga in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
